import {
  IGridFilterDefinition,
  GridFilterOperator,
  IGridFilter,
  GridFilterEntry,
  IDynamicValue,
  createFilter,
  GridFilterDefinitions,
  toGridDefinitionMap,
} from "@gtmhub/shared/components/grid-filtering-bar";
import {
  IListProperty,
  IListFilter,
  IDynamicFilter,
  DynamicFilters,
  IConfidenceValue,
  IListPropertyToGridFilterDefinitionContext,
  IGridFilterToListFilterContext,
  IConfidenceValueRange,
  IListSort,
} from "./models/models";
import { SelectAssigneeCustomFieldPredefinedValues, CustomFieldPredefinedValue } from "@gtmhub/customFields";
import { IConfidenceMapping, IConfidenceRange } from "@gtmhub/core";
import { localize } from "@gtmhub/localization";

const COFINDENCE_LEVEL_STEP = 0.1;

export const convertListPropertyToGridFilterDefinition = (property: IListProperty, context: IListPropertyToGridFilterDefinitionContext): IGridFilterDefinition => {
  let supportedOperators = property.supportedOperators as GridFilterOperator[];

  // For now we will not enable isBetween / isNotBetween for numeric filters on the client,
  // even though the API will return them. It will be solved in https://gtmhub.atlassian.net/browse/GVS-2362
  if (property.dataType === "numeric") {
    supportedOperators = supportedOperators.filter((o) => o !== "isBetween" && o !== "isNotBetween");
  }

  if (!supportedOperators.length) {
    console.warn("No supported operators in list property", property);
    return;
  }

  const defaultOperator = (property.defaultOperator || supportedOperators[0]) as GridFilterOperator;

  // we support isOneOf / isNotOneOf and contains / doesNotContain operators,
  // but we do not allow them in the UI for now for simplicity
  if (
    property.defaultOperator === "isOneOf" ||
    property.fieldName === "tags" ||
    property.fieldName === "teamIds" ||
    property.fieldName === "members" ||
    property.fieldName === "ownerIds" ||
    property.fieldName === "goalOwnerIds" ||
    property.fieldName === "userRoles"
  ) {
    supportedOperators = [defaultOperator];
  }

  const base = {
    fieldName: property.fieldName,
    displayName: property.displayName,
    trackingName: property.trackingName,
    defaultOperator,
    supportedOperators,
  };
  if (property.isCustom) {
    (base as Record<string, unknown>).isCustomField = true;
  }

  if (property.fieldName === "tags") {
    return { dataType: "array", arrayType: "tag", ...base };
  } else if (property.fieldName === "teamIds") {
    return { dataType: "array", arrayType: "team", ...base };
  } else if (property.fieldName === "ownerId" || property.fieldName === "ownerIds" || property.fieldName === "goalOwnerId" || property.fieldName === "goalOwnerIds") {
    return { dataType: "array", arrayType: "assignee", ...base };
  } else if (property.fieldName === "manager" || property.fieldName === "members" || property.fieldName === "createdById") {
    return { dataType: "array", arrayType: "user", ...base };
  } else if (property.fieldName === "userRoles") {
    return { dataType: "array", arrayType: "role", ...base };
  } else if (property.isCustom && property.dataType === "selectassignee") {
    const predefinedValues = property.predefinedValues as SelectAssigneeCustomFieldPredefinedValues;
    return { dataType: "array", arrayType: predefinedValues.targetGroup === "both" ? "assignee" : predefinedValues.targetGroup, ...base };
  } else if (property.fieldName === "parentGoalSessionId" || property.fieldName === "sessionId") {
    return { dataType: "array", arrayType: "session", ...base };
  } else if (property.fieldName === "parentId") {
    return { dataType: "array", arrayType: "okr", ...base };
  } else if (property.fieldName === "goalId") {
    return { dataType: "array", arrayType: "goal", ...base };
  } else if (property.fieldName === "closedStatus" || property.fieldName === "goalClosedStatus") {
    return { dataType: "array", arrayType: "okrStatus", ...base };
  } else if (property.fieldName === "workflow.status") {
    const entries: GridFilterEntry[] = [
      { value: "draft", displayName: localize("draft") },
      { value: "inReview", displayName: localize("in_review") },
      { value: "approved", displayName: localize("approved") },
      { value: "", displayName: localize("not_set_cap") },
    ];
    return { dataType: "array", arrayType: "static", keepOrder: true, entries, ...base };
  } else if (property.dataType === "date") {
    return { dataType: "date", ...base, local: property.isCustom };
  } else if (property.dataType === "numeric") {
    if (property.fieldName === "confidence.value") {
      let entries: GridFilterEntry[];
      if (context.confidenceSettings?.confidenceType === "numeric") {
        entries = (property.predefinedValues as CustomFieldPredefinedValue[]).map<GridFilterEntry>((value: number) => ({
          value,
          displayName: (context.confidenceScale === "major" ? value * 10 : value).toString(),
        }));
      } else if (context.confidenceSettings?.confidenceType === "emoji") {
        entries = context.confidenceSettings.confidenceMapping.map<GridFilterEntry>((mapping) => ({
          value: mapping.name,
          displayName: "",
          avatar: { emoji: mapping.name },
        }));
      } else if (context.confidenceSettings?.confidenceType === "text") {
        entries = context.confidenceSettings.confidenceMapping.map<GridFilterEntry>((mapping) => mapping.name);
      }

      return { dataType: "enum", entries, ...base };
    } else {
      const percentage = property.fieldName === "attainment" || property.fieldName === "progress" || property.fieldName === "goalAttainment";
      if (percentage) {
        (base as Record<string, unknown>).percentage = true;
      }
      return { dataType: "numeric", ...base };
    }
  } else if (property.dataType === "dropdown" && property.isCustom) {
    const entries = (property.predefinedValues as CustomFieldPredefinedValue[]).map<string>((v) => (typeof v === "object" ? v.value : v.toString()));
    return { dataType: "enum", entries, ...base };
  } else if (property.dataType === "multiselect" && property.isCustom) {
    const entries = (property.predefinedValues as CustomFieldPredefinedValue[]).map<string>((v) => (typeof v === "object" ? v.value : v.toString()));
    return { dataType: "array", arrayType: "static", entries, ...base };
  } else if (property.fieldName === "status") {
    const nonGtmhubStatuses = (property.predefinedValues as CustomFieldPredefinedValue[])
      .map<string>((v) => (typeof v === "object" ? v.value : v.toString()))
      .filter((status) => !!status && status !== "todo" && status !== "inprogress" && status !== "done");

    const entries: GridFilterEntry[] = [
      { value: "todo", avatar: { gtmhub: true }, displayName: localize("to_do") },
      { value: "inprogress", avatar: { gtmhub: true }, displayName: localize("in_progress") },
      { value: "done", avatar: { gtmhub: true }, displayName: localize("done") },
      ...nonGtmhubStatuses,
    ];
    return { dataType: "enum", entries, ...base };
  } else if (property.fieldName === "targetOperator") {
    const entries: GridFilterEntry[] = [
      { value: "at_least", displayName: localize("should_increase_to") },
      { value: "at_most", displayName: localize("should_decrease_to") },
      { value: "should_stay_above", displayName: localize("should_stay_above") },
      { value: "should_stay_below", displayName: localize("should_stay_below") },
      { value: "achieved_or_not", displayName: localize("achieved_or_not") },
    ];
    return { dataType: "enum", entries, ...base };
  } else if (property.dataType === "alphabetic") {
    return { dataType: "text", ...base };
  } else {
    console.warn("Cannot map list property", property, context);
  }
};

const convertGridFilterDynamicValuesToListFilterDynamicFilters = (filter: IGridFilter): IDynamicFilter[] => {
  if (filter.dataType !== "array" || !filter.dynamicValues || !filter.dynamicValues.length) {
    return;
  }

  const dynamicFilters: IDynamicFilter[] = [];
  for (const dynamicValue of filter.dynamicValues) {
    switch (dynamicValue.type) {
      case "me":
        dynamicFilters.push({
          dynamicValueType:
            filter.fieldName === "members"
              ? DynamicFilters.currentUserTeamsMember
              : filter.fieldName === "manager"
              ? DynamicFilters.currentUserTeamsManager
              : DynamicFilters.currentUser,
          value: dynamicValue.evaluated,
        });
        break;

      case "myTeams":
        dynamicFilters.push({ dynamicValueType: DynamicFilters.currentUserTeams, value: dynamicValue.evaluated });
        break;

      case "teamPlusMembers": {
        let dynamicFilter = dynamicFilters.find((f) => f.dynamicValueType === DynamicFilters.teamPlusMembers);
        if (!dynamicFilter) {
          dynamicFilter = { dynamicValueType: DynamicFilters.teamPlusMembers, value: [] };
          dynamicFilters.push(dynamicFilter);
        }
        dynamicFilter.value.push(dynamicValue.teamId);
        break;
      }

      case "teamOrg": {
        let dynamicFilter = dynamicFilters.find((f) => f.dynamicValueType === DynamicFilters.teamOrg);
        if (!dynamicFilter) {
          dynamicFilter = { dynamicValueType: DynamicFilters.teamOrg, value: [] };
          dynamicFilters.push(dynamicFilter);
        }
        dynamicFilter.value.push(dynamicValue.teamId);
        break;
      }

      case "activeSessions":
        dynamicFilters.push({ dynamicValueType: DynamicFilters.currentSessions, value: dynamicValue.evaluated });
        break;

      default:
        console.warn("Unknown grid dynamic filter value", dynamicValue);
        break;
    }
  }

  return dynamicFilters;
};

const convertListFilterDynamicFiltersToGridFilterDynamicValues = (listFilter: IListFilter): IDynamicValue[] => {
  const dynamicValues: IDynamicValue[] = [];
  for (const dynamicFilter of listFilter.dynamicFilters || []) {
    switch (dynamicFilter.dynamicValueType) {
      case DynamicFilters.currentUser:
      case DynamicFilters.currentUserTeamsManager:
      case DynamicFilters.currentUserTeamsMember:
        dynamicValues.push({ type: "me" });
        break;

      case DynamicFilters.currentSessions:
        dynamicValues.push({ type: "activeSessions" });
        break;

      case DynamicFilters.currentUserTeams:
        dynamicValues.push({ type: "myTeams" });
        break;

      case DynamicFilters.teamPlusMembers:
        dynamicValues.push(...(dynamicFilter.value || []).map<IDynamicValue>((teamId) => ({ type: "teamPlusMembers", teamId })));
        break;

      case DynamicFilters.teamOrg:
        dynamicValues.push(...(dynamicFilter.value || []).map<IDynamicValue>((teamId) => ({ type: "teamOrg", teamId })));
        break;

      default:
        console.warn("Cannot convert list dynamic filter to grid dynamic value", dynamicFilter);
    }
  }
  return dynamicValues;
};

const convertConfidenceLevelNonNumericGridFilterToListFilter = (filter: IGridFilter, property: IListProperty, context: IGridFilterToListFilterContext): IListFilter => {
  const correspondingConfidenceMapping = context.confidenceSettings.confidenceMapping.find((m) => m.name === filter.value);
  if (!correspondingConfidenceMapping) {
    console.warn("Confidence value filter is no longer applicable");
    return null;
  } else {
    const conversion = confidenceLevelGridFilterToListFilterMap.get(filter.operator);

    return {
      dataType: property.dataType,
      fieldName: filter.fieldName,
      operator: conversion.listOperator,
      value: conversion.convertValue(correspondingConfidenceMapping.range),
    };
  }
};

export const convertGridFiltersToListFilters = (
  filters: IGridFilter[],
  properties: Record<string, IListProperty>,
  context: IGridFilterToListFilterContext
): IListFilter[] => {
  return filters.map((filter) => convertGridFilterToListFilter(filter, properties[filter.fieldName], context)).filter((filter) => filter !== null);
};

const convertGridFilterToListFilter = (filter: IGridFilter, property: IListProperty, context: IGridFilterToListFilterContext): IListFilter => {
  if (filter.fieldName === "confidence.value" && context.confidenceSettings.confidenceType !== "numeric") {
    return convertConfidenceLevelNonNumericGridFilterToListFilter(filter, property, context);
  } else {
    const listFilter: IListFilter = {
      dataType: property.dataType,
      fieldName: filter.fieldName,
      operator: filter.operator,
      dynamicFilters: convertGridFilterDynamicValuesToListFilterDynamicFilters(filter),
      value: filter.value,
    };

    if (property.isCustom) {
      listFilter.customField = true;
    }
    return listFilter;
  }
};

export const convertListFiltersToGridFilters = (
  filterRules: IListFilter[],
  filterDefinitions: GridFilterDefinitions,
  context: IListPropertyToGridFilterDefinitionContext
): IGridFilter[] => {
  return filterRules
    .filter((rule) => rule.fieldName in filterDefinitions)
    .map((rule) => convertListFilterToGridFilter(rule, filterDefinitions[rule.fieldName], context))
    .filter((gridFilter) => gridFilter !== null);
};

const convertListFilterToGridFilter = (listFilter: IListFilter, definition: IGridFilterDefinition, context: IListPropertyToGridFilterDefinitionContext): IGridFilter => {
  const filter = createFilter(definition);

  if (listFilter.fieldName === "confidence.value" && context.confidenceSettings.confidenceType === "numeric" && typeof listFilter.value !== "number") {
    console.warn("Confidence value filter is no longer applicable");
    return null;
  }

  if (listFilter.fieldName === "confidence.value" && context.confidenceSettings.confidenceType !== "numeric") {
    const conversion = confidenceLevelListFilterToGridFilterMap.get(listFilter.operator);
    if (!conversion) {
      console.warn("Confidence value filter is no longer applicable");
      return null;
    }

    const filterValue = conversion.convertValue(listFilter.value, context.confidenceSettings.confidenceMapping);
    if (!filterValue) {
      console.warn("Confidence value filter is no longer applicable");
      return null;
    }

    filter.operator = conversion.filterOperator;
    filter.value = filterValue;
  } else {
    filter.operator = (listFilter.operator as GridFilterOperator) || definition.defaultOperator;
    filter.value = listFilter.value;
    if (filter.dataType === "array") {
      filter.dynamicValues = convertListFilterDynamicFiltersToGridFilterDynamicValues(listFilter);
    }
  }
  return filter;
};

/**
 * This map is meant to be used when the confidence type is not numeric - when it's text or emoji.
 * The numeric representation of a label or an emoji is not a single value, it's a range.
 * For example, if the user selects filter such as:
 * Confidence level equals Medium - we need to translate it to: Confidence level isBetween the range standing behind the Medium label.
 * That is why a conversion between the grid and list operators is needed. (ex. equals -> isBetween)
 * The confidence level range's inclusiveness/exclusiveness differs from what the list operators accept.
 * That is why the convertValue is needed. (ex. equals Medium = (0.3, 0.7] --> isBetween [0.4, 0.8))
 */
const confidenceLevelGridFilterToListFilterMap: Map<GridFilterOperator, { listOperator: string; convertValue(selectedRange: IConfidenceRange): IConfidenceValue }> =
  new Map([
    [
      // isBetween is working with intervals of type [)
      "equals",
      {
        listOperator: "isBetween",
        convertValue: (selectedRange) => {
          const value = {
            from: selectedRange.from === 0 ? selectedRange.from : selectedRange.from + COFINDENCE_LEVEL_STEP,
            to: selectedRange.to + COFINDENCE_LEVEL_STEP,
          };
          return confidenceValueToFixed(value);
        },
      },
    ],
    [
      // isNotBetween is working with intervals of type []
      "notEqual",
      {
        listOperator: "isNotBetween",
        convertValue: (selectedRange) => {
          const value = {
            from: selectedRange.from === 0 ? selectedRange.from : selectedRange.from + COFINDENCE_LEVEL_STEP,
            to: selectedRange.to,
          };
          return confidenceValueToFixed(value);
        },
      },
    ],
    [
      "greater",
      {
        listOperator: "greater",
        convertValue: (selectedRange) => selectedRange.to,
      },
    ],
    [
      "less",
      {
        listOperator: "less",
        convertValue: (selectedRange) => {
          const value = selectedRange.from === 0 ? selectedRange.from : selectedRange.from + COFINDENCE_LEVEL_STEP;
          return confidenceValueToFixed(value);
        },
      },
    ],
  ]);

/**
 * This map is meant to be used when the confidence type is not numeric - when it's text or emoji.
 * It is the confidenceLevelGridFilterToListFilterMap reverse map
 */
const confidenceLevelListFilterToGridFilterMap: Map<
  string,
  { filterOperator: GridFilterOperator; convertValue(selectedRange: IConfidenceValue, confidenceMappings?: IConfidenceMapping[]): string }
> = new Map([
  [
    "isBetween",
    {
      filterOperator: "equals",
      convertValue: (selectedRange: IConfidenceValueRange, confidenceMappings: IConfidenceMapping[]) => {
        const from = numberToFixed(selectedRange.from === 0 ? selectedRange.from : selectedRange.from - COFINDENCE_LEVEL_STEP);
        const to = numberToFixed(selectedRange.to - COFINDENCE_LEVEL_STEP);
        const correspondingConfidenceMapping = confidenceMappings.find((m) => m.range.to === to && m.range.from === from);
        return correspondingConfidenceMapping ? correspondingConfidenceMapping.name : null;
      },
    },
  ],
  [
    "isNotBetween",
    {
      filterOperator: "notEqual",
      convertValue: (selectedRange: IConfidenceValueRange, confidenceMappings: IConfidenceMapping[]) => {
        const from = numberToFixed(selectedRange.from === 0 ? selectedRange.from : selectedRange.from - COFINDENCE_LEVEL_STEP);
        const correspondingConfidenceMapping = confidenceMappings.find((m) => m.range.to === selectedRange.to && m.range.from === from);
        return correspondingConfidenceMapping ? correspondingConfidenceMapping.name : null;
      },
    },
  ],
  [
    "greater",
    {
      filterOperator: "greater",
      convertValue: (to: number, confidenceMappings: IConfidenceMapping[]) => {
        const correspondingConfidenceMapping = confidenceMappings.find((m) => m.range.to === to);
        return correspondingConfidenceMapping ? correspondingConfidenceMapping.name : null;
      },
    },
  ],
  [
    "less",
    {
      filterOperator: "less",
      convertValue: (from: number, confidenceMappings: IConfidenceMapping[]) => {
        const correspondingConfidenceMapping = confidenceMappings.find((m) => m.range.from === from);
        return correspondingConfidenceMapping ? correspondingConfidenceMapping.name : null;
      },
    },
  ],
]);

const numberToFixed = (value: number): number => {
  return Number(value.toFixed(1));
};

const confidenceValueToFixed = (value: IConfidenceValue): IConfidenceValue => {
  if (typeof value === "number") {
    value = numberToFixed(value);
  } else {
    value.from = Number(value.from.toFixed(1));
    value.to = Number(value.to.toFixed(1));
  }

  return value;
};

export const listsGridFilterDefinitions = (): GridFilterDefinitions => {
  return toGridDefinitionMap([
    {
      fieldName: "ownerId",
      displayName: localize("owner_cap"),
      trackingName: "Owner",
      dataType: "array",
      arrayType: "assignee",
      defaultOperator: "isOneOf",
      supportedOperators: ["isOneOf"],
    },
    {
      fieldName: "createdAt.time",
      displayName: localize("date_created_cap"),
      trackingName: "Date created",
      dataType: "date",
      defaultOperator: "isBefore",
      supportedOperators: ["isBefore", "isAfter", "isBetween", "isNotBetween"],
    },
  ]);
};

export const generateListsGridFilters = (filterDefinitions: GridFilterDefinitions): IGridFilter[] => {
  return [createFilter(filterDefinitions["ownerId"]), createFilter(filterDefinitions["createdAt.time"])];
};

export const toggleClosedStatusSort = (sort: IListSort): IListSort => {
  // what we mean by descending order in the webapp is treated as ascending on the api side
  return sort.fieldName === "closedStatus" || sort.fieldName === "goalClosedStatus" ? { ...sort, reversed: !sort.reversed } : sort;
};
